import axios from 'axios';
import { isLocalhost, isAuthBypassEnabled, getDummyAuthData } from '../helpers/devMode';

let instance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
});
let requestInterceptorId = null;
let responseInterceptorId = null;

// Create a navigation function that can be set from the app
let navigateToLogin = null;

export const setNavigateToLogin = (navigate) => {
  navigateToLogin = navigate;
};

/**
 * Gets the access token from sessionStorage
 * @returns {string|null} The access token or null if not found
 */
const getAccessToken = () => {
  // On localhost with auth bypass enabled, return dummy token
  if (isLocalhost() && isAuthBypassEnabled()) {
    return getDummyAuthData().accessToken;
  }
  
  return sessionStorage.getItem('accessToken');
};

/**
 * Gets the active system-log session identifier from sessionStorage.
 * @returns {string|null} Session identifier or null.
 */
const getSystemSessionId = () => {
  return sessionStorage.getItem('systemSessionId');
};

/**
 * Checks if the current request is for authentication endpoints
 * @param {string} url - The request URL
 * @returns {boolean} True if it's an auth endpoint
 */
const isAuthEndpoint = (url) => {
  const authEndpoints = ['/login', '/verify-mfa', '/check-mfa-status', '/auth/reset-password', '/system-in-maintenance'];
  return authEndpoints.some(endpoint => url.includes(endpoint));
};

/**
 * Checks if the current path is a static/public route that doesn't require authentication
 * @param {string} pathname - The current pathname
 * @returns {boolean} True if it's a static route
 */
const isStaticRoute = (pathname) => {
  const staticRoutes = [
    '/login', 
    '/forgot-password', 
    '/check-email', 
    '/change-password', 
    '/mfa-verification',
    '/portal/selection',
    '/unauthorized'
  ];
  return staticRoutes.includes(pathname);
};

/**
 * Redirects user to login page if not on a static route
 * @param {string} currentPath - The current path to return to after login
 */
const redirectToLogin = (currentPath) => {
  // On localhost with auth bypass enabled, don't redirect
  if (isLocalhost() && isAuthBypassEnabled()) {
    return;
  }
  
  const returnTo = encodeURIComponent(currentPath);
  if (navigateToLogin) {
    navigateToLogin(`/login?returnTo=${returnTo}`);
  } else {
    window.location.replace(`/login?returnTo=${returnTo}`);
  }
};

// Define a setup function to configure the interceptor
export const setupApi = () => {
  // Prevent duplicate interceptor registration across re-renders.
  if (requestInterceptorId !== null || responseInterceptorId !== null) {
    return;
  }

  // Request interceptor to add access token to headers and handle missing tokens
  requestInterceptorId = instance.interceptors.request.use(
    (config) => {
      // Don't add token for authentication endpoints to avoid deadlocks
      if (!isAuthEndpoint(config.url)) {
        const accessToken = getAccessToken();
        const systemSessionId = getSystemSessionId();
        if (systemSessionId) {
          config.headers['X-System-Session-Id'] = systemSessionId;
        }
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          // On localhost with auth bypass enabled, provide dummy token
          if (isLocalhost() && isAuthBypassEnabled()) {
            const dummyToken = getDummyAuthData().accessToken;
            config.headers.Authorization = `Bearer ${dummyToken}`;
            return config;
          }
          
          // Token not found - redirect to login if not on a static route
          const currentPath = window.location.pathname + window.location.search;
          if (!isStaticRoute(currentPath)) {
            redirectToLogin(currentPath);
            // Cancel the request since we're redirecting
            return Promise.reject(new Error('No access token found'));
          }
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle authentication errors
  responseInterceptorId = instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // On localhost with auth bypass enabled, don't handle 401/403 errors
      if (isLocalhost() && isAuthBypassEnabled()) {
        return Promise.reject(error);
      }
      
      // Handle 401 Unauthorized errors
      if (error.response?.status === 401) {
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        if (navigateToLogin) {
          navigateToLogin(`/login?returnTo=${returnTo}`);
        } else {
          window.location.replace(`/login?returnTo=${returnTo}`);
        }
      }
      
      // Handle 403 Forbidden errors
      if (error.response?.status === 403) {
        if (navigateToLogin) {
          navigateToLogin('/unauthorized');
        } else {
          window.location.replace('/unauthorized');
        }
      }
      
      return Promise.reject(error);
    }
  );
};

export default instance;